import { ROUTINE_READ_SCOPES, type RoutineReadScope } from "./types.ts";

export const MAX_ROUTINE_INSTRUCTIONS_CHARS = 8_000;

export type RoutineValidationInput = {
  timeOfDay: string;
  timezone: string;
  instructions: string;
  reads: readonly string[];
};

/** Whether a value is a zero-padded 24-hour `HH:MM` time. */
export function isTimeOfDay(value: unknown): value is string {
  return typeof value === "string" && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/** The spec defines a valid IANA zone as one accepted by `Intl.DateTimeFormat`. */
export function isIanaTimeZone(value: unknown): value is string {
  // ECMA-402 also accepts offset identifiers such as "+01:00"; routines require a named IANA zone.
  if (typeof value !== "string" || value.length === 0 || /^[+-]/.test(value)) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
    return true;
  } catch (error) {
    if (error instanceof RangeError) return false;
    throw error;
  }
}

export function isRoutineReadScope(value: unknown): value is RoutineReadScope {
  return typeof value === "string" && (ROUTINE_READ_SCOPES as readonly string[]).includes(value);
}

/** Readable, field-specific validation problems for create/edit routine inputs. */
export function routineProblems(input: RoutineValidationInput): string[] {
  const problems: string[] = [];
  if (!isTimeOfDay(input.timeOfDay)) problems.push("timeOfDay must be HH:MM in 24-hour time");
  if (!isIanaTimeZone(input.timezone)) problems.push("timezone must be a valid IANA time zone");
  if (input.instructions.length > MAX_ROUTINE_INSTRUCTIONS_CHARS) {
    problems.push(`instructions must be at most ${MAX_ROUTINE_INSTRUCTIONS_CHARS} characters`);
  }
  if (input.reads.length === 0) problems.push("reads must include at least one roomate: scope");
  for (const read of input.reads) {
    if (!isRoutineReadScope(read)) problems.push(`reads contains unsupported scope: ${read}`);
  }
  return problems;
}
